import React, { useState } from 'react';
import axios from 'axios';
import logo from "../../components/images/logo-transparent-png.png";
import { toast } from "react-hot-toast";
import { useNavigate, Link } from "react-router-dom";
import Header from "../../components/header/Header";
import Footer from "../../components/footer/Footer";
import EmailIcon from "@mui/icons-material/Email";
import LockResetIcon from "@mui/icons-material/LockReset";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [touched, setTouched] = useState(false);
    const [error, setError] = useState('');

    const apiUrl = process.env.REACT_APP_API;
    const navigate = useNavigate();

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const handleEmailChange = (event) => {
        const value = event.target.value;
        setEmail(value);

        if (touched) {
            if (!value.trim()) {
                setError('Email is required');
            } else if (!validateEmail(value)) {
                setError('Please enter a valid email address');
            } else {
                setError('');
            }
        }
    };

    const handleBlur = () => {
        setTouched(true);
        if (!email.trim()) {
            setError('Email is required');
        } else if (!validateEmail(email)) {
            setError('Please enter a valid email address');
        }
    };

    async function handleSubmit(event) {
        event.preventDefault();

        // Validate before submit
        if (!email.trim()) {
            setError('Email is required');
            setTouched(true);
            return;
        }

        if (!validateEmail(email)) {
            setError('Please enter a valid email address');
            setTouched(true);
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.post(`${apiUrl}/sendpasswordlink`, { email });
            setIsSuccess(true);
            toast.success("Password reset link sent to your email!");

            // Redirect after 3 seconds
            setTimeout(() => {
                navigate("/login");
            }, 3000);
        } catch (err) {
            console.error(err);
            setIsSubmitting(false);
            toast.error('Email not registered. Please check and try again.');
            setError('This email is not registered in our system');
        }
    }

    if (isSuccess) {
        return (
            <>
                <Header />
                <div className="forgot-password-page">
                    <div className="container py-5">
                        <div className="row justify-content-center">
                            <div className="col-md-6 col-lg-5">
                                <div className="success-card text-center">
                                    <div className="success-icon">
                                        <CheckCircleIcon style={{ fontSize: "5rem", color: "#4CAF50" }} />
                                    </div>
                                    <h2 className="success-title">Check Your Email!</h2>
                                    <p className="success-message">
                                        We've sent a password reset link to:
                                    </p>
                                    <p className="success-email">{email}</p>
                                    <p className="success-note">
                                        Click the link in the email to reset your password.
                                        If you don't see it, check your spam folder.
                                    </p>
                                    <div className="success-redirect">
                                        <small className="text-muted">
                                            Redirecting to login page in 3 seconds...
                                        </small>
                                    </div>
                                    <Link to="/login" className="btn back-to-login-button mt-3">
                                        Go to Login Now
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="forgot-password-page">
                <div className="container py-5">
                    <div className="row justify-content-center">
                        <div className="col-md-6 col-lg-5">
                            <div className="forgot-password-card">
                                {/* Logo */}
                                <div className="text-center mb-4">
                                    <img
                                        src={logo}
                                        alt="Logo"
                                        className="forgot-password-logo"
                                    />
                                </div>

                                {/* Icon */}
                                <div className="text-center mb-4">
                                    <div className="forgot-password-icon">
                                        <LockResetIcon style={{ fontSize: "3rem", color: "white" }} />
                                    </div>
                                </div>

                                {/* Title */}
                                <div className="text-center mb-4">
                                    <h2 className="forgot-password-title">Forgot Password?</h2>
                                    <p className="forgot-password-subtitle">
                                        No worries! Enter your email and we'll send you a reset link.
                                    </p>
                                </div>

                                {/* Form */}
                                <form onSubmit={handleSubmit}>
                                    <div className="form-group mb-4">
                                        <label htmlFor="email" className="form-label">
                                            <EmailIcon fontSize="small" className="me-1" />
                                            Email Address
                                        </label>
                                        <input
                                            type="email"
                                            className={`form-control form-control-lg ${
                                                touched && error ? 'is-invalid' : ''
                                            } ${touched && !error && email ? 'is-valid' : ''}`}
                                            id="email"
                                            placeholder="Enter your email address"
                                            value={email}
                                            onChange={handleEmailChange}
                                            onBlur={handleBlur}
                                            disabled={isSubmitting}
                                        />
                                        {touched && error && (
                                            <div className="invalid-feedback d-flex align-items-center">
                                                <ErrorOutlineIcon fontSize="small" className="me-1" />
                                                {error}
                                            </div>
                                        )}
                                        {touched && !error && email && (
                                            <div className="valid-feedback">
                                                Looks good!
                                            </div>
                                        )}
                                    </div>

                                    {/* Submit Button */}
                                    <button
                                        type="submit"
                                        className="btn reset-button w-100 mb-3"
                                        disabled={isSubmitting || (touched && error)}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" />
                                                Sending Reset Link...
                                            </>
                                        ) : (
                                            <>
                                                <LockResetIcon className="me-2" />
                                                Send Reset Link
                                            </>
                                        )}
                                    </button>

                                    {/* Back to Login */}
                                    <div className="text-center">
                                        <Link to="/login" className="back-link">
                                            <ArrowBackIcon fontSize="small" className="me-1" />
                                            Back to Login
                                        </Link>
                                    </div>
                                </form>

                                {/* Help Text */}
                                <div className="help-section mt-4">
                                    <p className="help-text">
                                        <strong>Note:</strong> The reset link will expire in 1 hour for security reasons.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />

            <style jsx>{`
        .forgot-password-page {
          min-height: calc(100vh - 200px);
          background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
          padding: 2rem 0;
        }

        .forgot-password-card {
          background: white;
          border-radius: 20px;
          padding: 3rem 2.5rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .forgot-password-logo {
          width: 180px;
          height: auto;
          filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
        }

        .forgot-password-icon {
          width: 100px;
          height: 100px;
          background: linear-gradient(135deg, #002e6a 0%, #c70044 100%);
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 10px 25px rgba(0, 46, 106, 0.3);
        }

        .forgot-password-title {
          font-size: 2rem;
          font-weight: 700;
          color: #002e6a;
          margin-bottom: 0.5rem;
        }

        .forgot-password-subtitle {
          font-size: 1rem;
          color: #666;
          margin: 0;
        }

        .form-label {
          font-weight: 600;
          color: #002e6a;
          margin-bottom: 0.5rem;
          display: flex;
          align-items: center;
        }

        .form-control-lg {
          padding: 0.875rem 1rem;
          font-size: 1rem;
          border-radius: 10px;
          border: 2px solid #e0e0e0;
          transition: all 0.3s ease;
        }

        .form-control-lg:focus {
          border-color: #002e6a;
          box-shadow: 0 0 0 0.2rem rgba(0, 46, 106, 0.15);
        }

        .form-control-lg.is-invalid {
          border-color: #dc3545;
        }

        .form-control-lg.is-valid {
          border-color: #28a745;
        }

        .invalid-feedback,
        .valid-feedback {
          font-size: 0.875rem;
          margin-top: 0.5rem;
          display: flex;
          align-items: center;
        }

        .invalid-feedback {
          color: #dc3545;
          display: block !important;
        }

        .valid-feedback {
          color: #28a745;
          display: block !important;
        }

        .reset-button {
          background: #c70044;
          color: white;
          border: none;
          padding: 1rem 1.5rem;
          font-size: 1.1rem;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .reset-button:hover:not(:disabled) {
          background: #a00036;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(199, 0, 68, 0.3);
        }

        .reset-button:disabled {
          background: #cccccc;
          cursor: not-allowed;
        }

        .back-link {
          color: #002e6a;
          text-decoration: none;
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          transition: color 0.2s ease;
        }

        .back-link:hover {
          color: #c70044;
          text-decoration: underline;
        }

        .help-section {
          background: #e7f3ff;
          border-left: 4px solid #002e6a;
          padding: 1rem;
          border-radius: 8px;
        }

        .help-text {
          margin: 0;
          font-size: 0.9rem;
          color: #004085;
        }

        .success-card {
          background: white;
          border-radius: 20px;
          padding: 4rem 3rem;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
        }

        .success-icon {
          margin-bottom: 2rem;
        }

        .success-title {
          font-size: 2rem;
          font-weight: 700;
          color: #4CAF50;
          margin-bottom: 1rem;
        }

        .success-message {
          font-size: 1.1rem;
          color: #666;
          margin-bottom: 0.5rem;
        }

        .success-email {
          font-size: 1.25rem;
          font-weight: 600;
          color: #002e6a;
          margin-bottom: 1.5rem;
        }

        .success-note {
          font-size: 1rem;
          color: #666;
          line-height: 1.6;
          margin-bottom: 2rem;
        }

        .success-redirect {
          margin-bottom: 1rem;
        }

        .back-to-login-button {
          background: #002e6a;
          color: white;
          border: none;
          padding: 0.875rem 2rem;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 10px;
          transition: all 0.3s ease;
        }

        .back-to-login-button:hover {
          background: #001a40;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 46, 106, 0.3);
        }

        @media (max-width: 768px) {
          .forgot-password-card,
          .success-card {
            padding: 2rem 1.5rem;
          }

          .forgot-password-logo {
            width: 150px;
          }

          .forgot-password-title,
          .success-title {
            font-size: 1.75rem;
          }

          .forgot-password-subtitle {
            font-size: 0.95rem;
          }

          .forgot-password-icon {
            width: 80px;
            height: 80px;
          }
        }

        @media (max-width: 576px) {
          .forgot-password-card,
          .success-card {
            padding: 1.5rem 1.25rem;
          }

          .forgot-password-logo {
            width: 130px;
          }

          .forgot-password-title,
          .success-title {
            font-size: 1.5rem;
          }
        }
      `}</style>
        </>
    );
}

export default ForgotPasswordPage;